import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InputAdornment, OutlinedInput } from '@mui/material';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MY } from '../../config/fe-routes';
import { useAttributionOrigin } from '../../hooks/useAttributionOrigin';
import { usePriceFormatter } from '../../hooks/usePriceFormatter';
import OfferService from '../../services/offer.service';
import ProductService from '../../services/product.service';
import UserService from '../../services/user.service';
import { ProductType } from '../../types/product.types';
import { getCurrencySymbol, stringToPrice } from '../../utils/price-format';
import { redirectTo } from '../../utils/route-helpers';
import { notification } from '../../utils/snackbar';
import Loader from '../Loader/Loader';
import { OfferInfoModal } from '../OfferInfoModal/OfferInfoModal';
import Button from '../ui-kit/Button/Button';
import Modal from '../ui-kit/Modal/Modal';

const productService = new ProductService();
const offerService = new OfferService();
const userService = new UserService();

export interface MakeOfferModalProps {
  productId?: number;
  buyerId?: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  disableRedirect?: boolean;
}

type OfferPreset = '10' | '20' | 'custom';

const PRESETS: { preset: OfferPreset; discount: number }[] = [
  { preset: '10', discount: 0.1 },
  { preset: '20', discount: 0.2 },
  { preset: 'custom', discount: 0 },
];

const MakeOfferModal = ({ productId, buyerId, open, onClose, onSuccess, disableRedirect }: MakeOfferModalProps) => {
  const { t } = useTranslation();
  const formatPrice = usePriceFormatter();
  const { showErrorNotification } = notification;

  const [product, setProduct] = useState<ProductType | null>(null);
  const attributionOrigin = useAttributionOrigin(product?.slug);
  const [isLoading, setIsLoading] = useState(true);
  const [offerPrice, setOfferPrice] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<OfferPreset>('custom');
  const [isSaving, setIsSaving] = useState(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);

  useEffect(() => {
    if (!open || !productId) {
      return;
    }

    setIsLoading(true);
    setProduct(null);
    setOfferPrice('');
    setSelectedPreset('custom');

    productService
      .getProductById(productId)
      .then((data) => {
        setProduct(data);
      })
      .catch((error) => {
        showErrorNotification({ error });
      })
      .finally(() => setIsLoading(false));
  }, [open, productId]);

  const productPrice = product ? +product.price : 0;

  const getPresetPrice = (discount: number) => Math.round(productPrice * (1 - discount) * 100) / 100;

  const selectPreset = (preset: OfferPreset, discount: number) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      setOfferPrice(String(getPresetPrice(discount)));
    }
  };

  const totalWithFee = (() => {
    const price = +offerPrice;

    if (!price || !product) {
      return null;
    }

    const fee = price * ((product.buyerSideFeesPercentage || 0) / 100) + (product.buyerSideFeesFixed || 0);

    return Math.round((price + fee) * 100) / 100;
  })();

  const handleSendOffer = async () => {
    if (!product || !offerPrice?.trim()) {
      return;
    }

    try {
      setIsSaving(true);
      if (buyerId) {
        await offerService.createSellerOffer({ productId: product.id, buyerId, offeredPrice: +offerPrice }, { silentFail: true });
      } else {
        await offerService.createOffer({ productId: product.id, offeredPrice: +offerPrice, ...attributionOrigin }, { silentFail: true });
      }
      setIsSaving(false);
      onClose();
      notification.showSuccessNotification(t('common.offer-made'));
      onSuccess?.();
      if (buyerId && !disableRedirect) {
        try {
          const user = await userService.getUserById(buyerId);
          const shopSlug = user?.shop?.active_slug;

          if (shopSlug) {
            redirectTo(MY.CONVERSATION(shopSlug));
          }
        } catch {
          // redirect failed silently
        }
      }
    } catch (error) {
      setIsSaving(false);
      showErrorNotification({ error });
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} showCloseButton className="make-offer-modal" title={t('common.make-offer')}>
        {isLoading && <Loader my={3} />}

        {!isLoading && product && (
          <>
            <div className="make-offer-modal__product">
              {product.images?.[0] && (
                <img className="make-offer-modal__product-image" src={product.images[0].thumbnail} alt={product.name} />
              )}
              <div className="make-offer-modal__product-details">
                <p className="make-offer-modal__product-name">
                  {product.description?.length > 100 ? `${product.description.slice(0, 100)}…` : product.description}
                </p>
                {(product?.brand || product?.size) && (
                  <p className="make-offer-modal__product-meta">
                    <span>{[product.brand, product.size].filter(Boolean).join(', ')}</span>
                  </p>
                )}
                <p className="make-offer-modal__product-price">{formatPrice(product.price)}</p>
              </div>
            </div>

            <div className="make-offer-modal__content">
              <div className="make-offer-modal__presets">
                {PRESETS.map(({ preset, discount }) => (
                  <button
                    key={preset}
                    className={classNames('make-offer-modal__preset', {
                      'make-offer-modal__preset--selected': selectedPreset === preset,
                    })}
                    onClick={() => selectPreset(preset, discount)}
                  >
                    <span className="make-offer-modal__preset-value">
                      {preset !== 'custom' ? formatPrice(getPresetPrice(discount)) : t('common.custom')}
                    </span>
                    <span className="make-offer-modal__preset-label">
                      {preset !== 'custom' ? t('common.off', { percentage: discount * 100 }) : t('common.set-price')}
                    </span>
                  </button>
                ))}
              </div>

              <OutlinedInput
                fullWidth
                className="make-offer-modal__price-input"
                startAdornment={
                  <InputAdornment position="start">
                    <span className="make-offer-modal__currency">{getCurrencySymbol()}</span>
                  </InputAdornment>
                }
                value={offerPrice}
                onChange={(e) => {
                  setSelectedPreset('custom');
                  setOfferPrice(stringToPrice(e.target.value));
                }}
              />

              {product.buyerProtectionFeeEnabled && totalWithFee !== null && (
                <p className="make-offer-modal__fee-caption">
                  {t('common.with-buyer-protection-fee', { amount: formatPrice(totalWithFee) })}
                </p>
              )}

              <Button
                buttonType="primary"
                fullWidth
                disabled={isSaving || !offerPrice?.trim() || +offerPrice >= productPrice}
                onClick={handleSendOffer}
                className="make-offer-modal__send-btn"
                size="large"
              >
                {t('common.send-offer')}
              </Button>

              <button className="make-offer-modal__info-link" onClick={() => setInfoModalOpen(true)}>
                {t('pages.offer.learn-more')}
                <InfoOutlinedIcon className="make-offer-modal__info-icon" />
              </button>
            </div>
          </>
        )}
      </Modal>

      <OfferInfoModal isOffersModalOpen={isInfoModalOpen} setOffersModalOpen={setInfoModalOpen} hideBackdrop />
    </>
  );
};

export default MakeOfferModal;
